import { useEffect } from 'react';
import type { Viewport } from '@/theme/breakpoints';
import { LogLevel, useSeqLog } from '@/hooks/telemetry';
import { captureElementHierarchy, captureElementLayoutSnapshot } from '@/hooks/telemetry/dom-layout-snapshot';
import {
  buildViewportMetrics,
  measureRawViewportDimensions,
} from '@/hooks/dom/viewport-metrics-measurement';
import { ModalLocation } from '@/constants/componentPresets';
import {
  CMODAL_CENTER_SNAPSHOT_DELAY_MS,
  CMODAL_LAYOUT_HIERARCHY_DEPTH,
  CMODAL_SCROLL_DEBUG_FEATURE,
  CMODAL_SEQ_COMPONENT,
} from './Cmodal.constants';
import type { CmodalElementRefs } from './Cmodal.types';

enum CmodalScrollDebugReason {
  SideModalVisible = 'side-modal-visible',
}

interface UseCmodalTelemetryInput extends CmodalElementRefs {
  visible: boolean;
  location: ModalLocation;
  menuTitle?: string;
  titleExtra?: string;
  isMobile: boolean;
  viewport: Viewport | null;
}

interface ModalLayoutSnapshotInput {
  containerElement: HTMLElement | null;
  contentWrapperElement: HTMLElement | null;
  tileWrapperElement: HTMLElement | null;
  tileRootElement: HTMLElement | null;
  contentElement: HTMLElement | null;
  mainContentElement: HTMLElement | null;
}

interface CenterModalLayoutSnapshotInput {
  containerElement: HTMLElement | null;
  contentWrapperElement: HTMLElement | null;
  tileWrapperElement: HTMLElement | null;
  contentElement: HTMLElement | null;
  mainContentElement: HTMLElement | null;
  isMobile: boolean;
}

interface ElementRectSnapshot {
  x: number | undefined;
  width: number | undefined;
  right: number | undefined;
}

interface ContainerCenterSnapshot extends ElementRectSnapshot {
  scrollWidth: number | undefined;
  clientWidth: number | undefined;
  overflowY: string | null;
}

interface ContentWrapperCenterSnapshot extends ElementRectSnapshot {
  cssWidth: string | null;
  cssMaxWidth: string | null;
}

function getElementRect(element: HTMLElement | null): DOMRect | null {
  if (!element) {
    return null;
  }

  return element.getBoundingClientRect();
}

function getElementComputedStyle(element: HTMLElement | null): CSSStyleDeclaration | null {
  if (!element) {
    return null;
  }

  return window.getComputedStyle(element);
}

function buildElementRectSnapshot(element: HTMLElement | null): ElementRectSnapshot {
  const rect = getElementRect(element);

  return {
    x: rect?.x,
    width: rect?.width,
    right: rect?.right,
  };
}

function buildContainerCenterSnapshot(element: HTMLElement | null): ContainerCenterSnapshot {
  const rectSnapshot = buildElementRectSnapshot(element);
  const computedStyle = getElementComputedStyle(element);

  return {
    ...rectSnapshot,
    scrollWidth: element?.scrollWidth,
    clientWidth: element?.clientWidth,
    overflowY: computedStyle ? computedStyle.overflowY : null,
  };
}

function buildContentWrapperCenterSnapshot(element: HTMLElement | null): ContentWrapperCenterSnapshot {
  const rectSnapshot = buildElementRectSnapshot(element);
  const computedStyle = getElementComputedStyle(element);

  return {
    ...rectSnapshot,
    cssWidth: computedStyle ? computedStyle.width : null,
    cssMaxWidth: computedStyle ? computedStyle.maxWidth : null,
  };
}

function buildCenterModalLayoutSnapshot({
  containerElement,
  contentWrapperElement,
  tileWrapperElement,
  contentElement,
  mainContentElement,
  isMobile,
}: CenterModalLayoutSnapshotInput): Record<string, unknown> {
  const container = buildContainerCenterSnapshot(containerElement);
  const contentWrapper = buildContentWrapperCenterSnapshot(contentWrapperElement);
  const tileWrapper = buildElementRectSnapshot(tileWrapperElement);
  const content = buildElementRectSnapshot(contentElement);
  const mainContent = buildElementRectSnapshot(mainContentElement);

  return {
    windowInnerWidth: window.innerWidth,
    bodyZoom: window.getComputedStyle(document.body).zoom,
    isMobile,
    containerX: container.x,
    containerW: container.width,
    containerScrollW: container.scrollWidth,
    containerClientW: container.clientWidth,
    containerOverflowY: container.overflowY,
    cwX: contentWrapper.x,
    cwW: contentWrapper.width,
    cwRight: contentWrapper.right,
    cwCSSWidth: contentWrapper.cssWidth,
    cwCSSMaxWidth: contentWrapper.cssMaxWidth,
    twX: tileWrapper.x,
    twW: tileWrapper.width,
    contentX: content.x,
    contentW: content.width,
    mainX: mainContent.x,
    mainW: mainContent.width,
    mainClientW: mainContentElement?.clientWidth,
  };
}

function buildModalLayoutSnapshot({
  containerElement,
  contentWrapperElement,
  tileWrapperElement,
  tileRootElement,
  contentElement,
  mainContentElement,
}: ModalLayoutSnapshotInput): Record<string, unknown> {
  return {
    container: captureElementLayoutSnapshot({
      element: containerElement,
      label: 'cmodal-container',
    }),
    contentWrapper: captureElementLayoutSnapshot({
      element: contentWrapperElement,
      label: 'cmodal-content-wrapper',
    }),
    tileWrapper: captureElementLayoutSnapshot({
      element: tileWrapperElement,
      label: 'cmodal-tile-wrapper',
    }),
    tileRoot: captureElementLayoutSnapshot({
      element: tileRootElement,
      label: 'cmodal-tile-root',
    }),
    content: captureElementLayoutSnapshot({
      element: contentElement,
      label: 'cmodal-content',
    }),
    mainContent: captureElementLayoutSnapshot({
      element: mainContentElement,
      label: 'cmodal-main-content',
    }),
    mainContentHierarchy: captureElementHierarchy({
      element: mainContentElement,
      label: 'cmodal-main-content-hierarchy',
      maxDepth: CMODAL_LAYOUT_HIERARCHY_DEPTH,
    }),
  };
}

export function useCmodalTelemetry({
  visible,
  location,
  menuTitle,
  titleExtra,
  isMobile,
  viewport,
  containerRef,
  contentWrapperRef,
  tileWrapperRef,
  tileRootRef,
  contentRef,
  mainContentRef,
}: UseCmodalTelemetryInput): void {
  const log = useSeqLog(CMODAL_SEQ_COMPONENT, {
    feature: CMODAL_SCROLL_DEBUG_FEATURE,
    location,
    menuTitle: menuTitle ?? null,
  });

  useEffect((): (() => void) | undefined => {
    if (!visible || location !== ModalLocation.Center) {
      return undefined;
    }

    const id = window.setTimeout((): void => {
      log(
        'Cmodal center bbox snapshot',
        buildCenterModalLayoutSnapshot({
          containerElement: containerRef.current,
          contentWrapperElement: contentWrapperRef.current,
          tileWrapperElement: tileWrapperRef.current,
          contentElement: contentRef.current,
          mainContentElement: mainContentRef.current,
          isMobile,
        }),
        LogLevel.Info,
      );
    }, CMODAL_CENTER_SNAPSHOT_DELAY_MS);

    return (): void => {
      window.clearTimeout(id);
    };
  }, [containerRef, contentRef, contentWrapperRef, isMobile, location, log, mainContentRef, tileWrapperRef, visible]);

  useEffect((): (() => void) | undefined => {
    if (!visible || location !== ModalLocation.Left) {
      return undefined;
    }

    const frame = requestAnimationFrame((): void => {
      log(
        'Cmodal: side modal layout snapshot',
        {
          reason: CmodalScrollDebugReason.SideModalVisible,
          location,
          isMobile,
          viewport: viewport ?? null,
          viewportHeightUnit: buildViewportMetrics(
            measureRawViewportDimensions(),
          ).vh,
          windowInnerWidth: window.innerWidth,
          windowInnerHeight: window.innerHeight,
          ...buildModalLayoutSnapshot({
            containerElement: containerRef.current,
            contentWrapperElement: contentWrapperRef.current,
            tileWrapperElement: tileWrapperRef.current,
            tileRootElement: tileRootRef.current,
            contentElement: contentRef.current,
            mainContentElement: mainContentRef.current,
          }),
        },
        LogLevel.Info,
      );
    });

    return (): void => {
      cancelAnimationFrame(frame);
    };
  }, [
    containerRef,
    contentRef,
    contentWrapperRef,
    isMobile,
    location,
    log,
    mainContentRef,
    menuTitle,
    tileRootRef,
    tileWrapperRef,
    titleExtra,
    viewport,
    visible,
  ]);
}
